import { useCallback, useMemo, useRef, useState } from 'react';

import { resolveGutBodyConnectionsSystems } from '#components/GutBodyConnectionsBody/systems';
import type { OrganArtworkQuality } from '#assets/images/gutBodyConnections';
import type {
  GutBodyConnectionsColorVariant,
  GutBodyConnectionsOrgan,
} from '@repo/types';

type UseGutBodyConnectionsImagesArgs = {
  organs: GutBodyConnectionsOrgan[];
  /** `kit.age_at_sampling`, in days. */
  age: number;
  pregnant: boolean;
  sex: string;
  quality?: OrganArtworkQuality;
  waitForImages?: boolean;
  colorVariant?: GutBodyConnectionsColorVariant;
};

/**
 * Reports when a body's drawings have all loaded, so the organs appear together
 * rather than one at a time.
 *
 * The count comes from the same parts that get rendered: an organ can own two
 * drawings or none, so any separately derived total would deadlock the paediatric
 * bodies, where fewer organs render than the kit has.
 */
export function useGutBodyConnectionsImages({
  organs,
  age,
  pregnant,
  sex,
  quality,
  waitForImages = true,
  colorVariant,
}: UseGutBodyConnectionsImagesArgs) {
  const resolved = useMemo(
    () =>
      resolveGutBodyConnectionsSystems({
        organs,
        age,
        pregnant,
        sex,
        quality,
        colorVariant,
      }),
    [organs, age, pregnant, sex, quality, colorVariant],
  );

  const partCount = resolved.systems.reduce(
    (total, system) => total + system.parts.length,
    0,
  );

  const [loaded, setLoaded] = useState<ReadonlySet<string>>(new Set());

  // The img `ref` below is a fresh closure each render, so React re-attaches it
  // and re-marks every cached drawing; without this guard that schedules an
  // update per render and the page loops.
  const markedRef = useRef<Set<string>>(new Set());

  const mark = useCallback(
    (part: string) => {
      if (!waitForImages || markedRef.current.has(part)) return;
      markedRef.current.add(part);
      setLoaded(new Set(markedRef.current));
    },
    [waitForImages],
  );

  const imgProps = useCallback(
    (part: string) => ({
      // A cached drawing can finish before React attaches onLoad, so count it here.
      ref: (img: HTMLImageElement | null) => {
        if (img?.complete) mark(part);
      },
      onLoad: () => mark(part),
      // A drawing that fails still counts, or one bad asset holds the body forever.
      onError: () => mark(part),
    }),
    [mark],
  );

  return {
    ...resolved,
    imgProps,
    ready: !waitForImages || loaded.size >= partCount + 1,
  };
}
